import { html, TemplateResult } from 'lit-html';

import { TAX_OPTIONS } from '../../services/constants';
import buildProductCard from '../../services/productFactory';
import globalStyleClass from '../../services/globalStyles';
import { ProductProps } from '../../types/productProps';

import { defaultImageIcon } from './icons';
import ratingStars from './ratingStars/ratingStars';
import {
    actionsStyle,
    brandStyle,
    buttonStyle,
    contentStyle,
    productCardStyle,
    productDefaultImageStyle,
    productImageContainer,
    productImageStyle,
    productNameStyle,
    productPriceStyle,
    productRatingStyle,
} from './styles';

const product = (productProps: ProductProps): TemplateResult => {
    const productCardProps = buildProductCard(productProps);
    const productCurrencyNode = productProps._.data && productProps._.data.site && productProps._.data.site.currency;

    const productImageHTML = html`
        <div class="${productImageStyle(productCardProps, productProps)}"></div>
    `;

    const defaultProductImage = html`
        <div class="${productDefaultImageStyle(productProps)}">
            ${defaultImageIcon}
        </div>
    `;

    const productImage = html`
        <a href="${productCardProps.path}" class="${productImageContainer}" data-test-id="product-widget-image">
            ${productCardProps.productImage ? productImageHTML : defaultProductImage}
        </a>
    `;

    const productName = html`
        <div
            class="${productNameStyle(productProps)} ${globalStyleClass(productProps.productName.textStyle)}"
            data-test-id="product-widget-name"
        >
            <a href="${productCardProps.path}">${productCardProps.name}</a>
        </div>
    `;

    const productBrand = html`
        <div
            class="${brandStyle(productProps)} ${globalStyleClass(productProps.brand.textStyle)}"
            data-test-id="product-widget-brand"
        >
            ${productCardProps.brand}
        </div>
    `;

    const productRatings = html`
        <div class="${productRatingStyle(productProps)}" data-test-id="product-widget-ratings">
            ${ratingStars(productCardProps.rating, productCardProps.starColorFilled, productCardProps.starColorEmpty)}
            <span>(${productCardProps.totalReviews})</span>
        </div>
    `;

    const formatPrice = (price: number): string => {
        const { symbol, symbolPlacement, decimalToken, decimalPlaces, thousandsToken } = productCurrencyNode.display;

        const replaceTokens = { '.': decimalToken, ',': thousandsToken };
        const formatDecimalPrice = price
            .toLocaleString('en-US', { minimumFractionDigits: decimalPlaces })
            .replace(/[.,]/g, match => replaceTokens[match]);

        return symbolPlacement === 'LEFT' ? `${symbol}${formatDecimalPrice}` : `${formatDecimalPrice}${symbol}`;
    };

    const productDisplayPrice = (price: number): string => {
        if (productCurrencyNode) {
            return formatPrice(price);
        }

        // If currencyNode is null, return intl format
        return new Intl.NumberFormat(productCardProps.displayLocale, {
            style: 'currency',
            currency: productCardProps.currencyCode,
        }).format(price);
    };

    const getProductPriceWithTax = (): TemplateResult => {
        let displayPriceWithTax = '';
        if (productCardProps.priceWithTax && productCardProps.currencyCode) {
            displayPriceWithTax = productDisplayPrice(productCardProps.priceWithTax);
        }

        return html`
            <div
                class="${productPriceStyle(productProps)} ${globalStyleClass(productProps.price.textStyle)}"
                data-test-id="product-widget-price"
            >
                ${displayPriceWithTax}
            </div>
        `;
    };

    const getProductPriceWithoutTax = (): TemplateResult => {
        let displayPriceWithoutTax = '';
        if (productCardProps.priceWithoutTax && productCardProps.currencyCode) {
            displayPriceWithoutTax = productDisplayPrice(productCardProps.priceWithoutTax);
        }

        return html`
            <div
                class="${productPriceStyle(productProps)} ${globalStyleClass(productProps.price.textStyle)}"
                data-test-id="product-widget-price"
            >
                ${displayPriceWithoutTax}
            </div>
        `;
    };

    const getProductPriceWithAndWithoutTax = (): TemplateResult => {
        let displayPriceWithTax = '';
        if (productCardProps.priceWithTax && productCardProps.currencyCode) {
            const formatter = new Intl.NumberFormat(productCardProps.displayLocale, {
                style: 'currency',
                currency: productCardProps.currencyCode,
            });
            displayPriceWithTax = `${formatter.format(productCardProps.priceWithTax)} (Inc. Tax)`;
        }

        let displayPriceWithoutTax = '';
        if (productCardProps.priceWithoutTax && productCardProps.currencyCode) {
            const formatter = new Intl.NumberFormat(productCardProps.displayLocale, {
                style: 'currency',
                currency: productCardProps.currencyCode,
            });
            displayPriceWithoutTax = `${formatter.format(productCardProps.priceWithoutTax)} (Ex. Tax)`;
        }

        return html`
            <div
                class="${productPriceStyle(productProps)} ${globalStyleClass(productProps.price.textStyle)}"
                data-test-id="product-widget-price"
            >
                <div>${displayPriceWithTax}</div>
                <div>${displayPriceWithoutTax}</div>
            </div>
        `;
    };

    const getProductPrice = (): TemplateResult => {
        switch (productCardProps.plpTaxSetting) {
            case TAX_OPTIONS.INCLUDED:
                return getProductPriceWithTax();
            case TAX_OPTIONS.EXCLUDED:
                return getProductPriceWithoutTax();
            case TAX_OPTIONS.BOTH:
                return getProductPriceWithAndWithoutTax();
            default:
                return getProductPriceWithoutTax();
        }
    };

    const productPrice = getProductPrice();

    const productActionButton = html`
        <div
            class="${actionsStyle({
                ...productProps,
                button: { ...productProps.button, visibility: productCardProps.buttonVisibility },
            })}
                    ${globalStyleClass(productProps.button.textStyle)}"
        >
            <a href="${productCardProps.actionLink}" class="${buttonStyle(productProps)}">
                ${productCardProps.actionLabel}
            </a>
        </div>
    `;

    return html`
        <div class="${productCardStyle}">
            <div class="${contentStyle(productProps)}" aria-label="${productCardProps.name}">
                ${productImage} ${productBrand} ${productName} ${productRatings} ${productPrice} ${productActionButton}
            </div>
        </div>
    `;
};

export default product;
